import { useCallback, useState } from 'react';

type UseLocalStorageReturn<T> = [T | null, (value: T) => void];

type UseLocalStorageParams<T> = {
  initialValue?: T;
};

const useLocalStorage = <T>(key: string, { initialValue }: UseLocalStorageParams<T> = {}): UseLocalStorageReturn<T> => {
  const getStorage = useCallback((storageKey: string): T | null => {
    try {
      const item = localStorage.getItem(storageKey);
      if (!item) return null;
      return JSON.parse(item) as T;
    } catch {
      throw new Error('Failed to get value from localStorage - useLocalStorage');
    }
  }, []);

  const setStorage = useCallback(
    (storageKey: string, storageValue: T): T | null => {
      try {
        localStorage.setItem(storageKey, JSON.stringify(storageValue));
        return getStorage(storageKey);
      } catch {
        throw new Error('Failed to set value in localStorage - useLocalStorage');
      }
    },
    [getStorage],
  );

  const getInitialValue = () => {
    const storedValue = getStorage(key);

    if (storedValue !== null) return storedValue;
    if (initialValue !== undefined) return setStorage(key, initialValue);

    return null;
  };

  const [value, setValue] = useState<T | null>(() => getInitialValue());

  const handleSetValue = useCallback(
    (newValue: T) => {
      setValue(setStorage(key, newValue));
    },
    [key, setStorage],
  );

  return [value, handleSetValue];
};

export { useLocalStorage };
